import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import "./clear-values-heading.css";

function ClearLetters() {
  return (
    <>
      <span className="clear-values-heading__label">CLEAR</span>
      <span className="clear-values-heading__letters" aria-hidden="true">
        {"CLEAR".split("").map((letter) => (
          <span className="clear-values-heading__letter" key={letter}>
            {letter}
          </span>
        ))}
      </span>
    </>
  );
}

/** Homepage navigation keeps one accessible label and the existing growing underline. */
export function ClearValuesLink() {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!window.matchMedia || !window.IntersectionObserver || !linkRef.current) return;
    const motion = window.matchMedia("(hover: none) and (prefers-reduced-motion: no-preference)");
    let played = false;
    let observer: IntersectionObserver | undefined;
    const update = () => {
      observer?.disconnect();
      if (!motion.matches) {
        setPulse(false);
        return;
      }
      if (played) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 1))
            return;
          played = true;
          observer?.disconnect();
          setPulse(true);
        },
        { threshold: 1 },
      );
      observer.observe(linkRef.current!);
    };
    update();
    motion.addEventListener("change", update);
    return () => {
      observer?.disconnect();
      motion.removeEventListener("change", update);
    };
  }, []);

  return (
    <span className="clear-values-heading__link-group">
      <Link
        ref={linkRef}
        className="clear-values-heading__word"
        to="/about#clear-values"
        aria-label="CLEAR Values"
        data-clear-pulse={pulse ? "true" : undefined}
        onAnimationEnd={(event) => {
          if (event.animationName.startsWith("clear-highlight-")) setPulse(false);
        }}
      >
        <ClearLetters />
      </Link>
      <span className="clear-values-heading__suffix" aria-hidden="true">
        Values
      </span>
    </span>
  );
}

/** The About signature is decorative; CLEAR remains one readable word for assistive technology. */
export function ClearValuesHeading() {
  return (
    <div className="clear-signature clear-values-heading">
      <span>Our</span>{" "}
      <strong className="clear-values-heading__word">
        <ClearLetters />
      </strong>{" "}
      <span>Values</span>
    </div>
  );
}
